import {
  EmptyState,
  EmptyStateIcon,
  Spinner,
  Title,
} from '@patternfly/react-core'
import { SearchIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { useProgramInteractionRecords } from '@app/domain/program/program.hooks'
import type { IProgram } from '@app/domain/program/program.interface'
import type { ISortDirection } from '@app/lib/sorting'

import { ConfiguredInteractionsTable } from './configured-interactions-table'
import { ConfiguredInteractionsToolbar } from './configured-interactions-toolbar'

interface Props {
  program: IProgram
}

export const ConfiguredInteractions = ({ program }: Props) => {
  const history = useHistory()

  const {
    interactions,
    fetchProgramInteractionRecords,
    total,
    isLoading,
    sendInteractionToIntakeAnywhere,
    revokeRemoteInteraction,
  } = useProgramInteractionRecords()

  const [patientName, setPatientName] = useState('')
  const [mrn, setMrn] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [recordsPerPage, setRecordsPerPage] = useState(10)
  const [sortDirection, setSortDirection] = useState<ISortDirection>(null)
  const [sortField, setSortField] = useState<string>(null)
  const [isDeliverable, setDeliverable] = useState<boolean | undefined>(
    undefined,
  )
  const [status, setStatus] = useState<string>(null)

  const handleRefresh = useCallback(() => {
    fetchProgramInteractionRecords(
      program?.programId,
      pageNumber,
      recordsPerPage,
      patientName,
      mrn,
      isDeliverable,
      status,
      sortField,
      sortDirection,
    )
  }, [
    fetchProgramInteractionRecords,
    program?.programId,
    pageNumber,
    recordsPerPage,
    patientName,
    mrn,
    isDeliverable,
    status,
    sortField,
    sortDirection,
  ])

  useEffect(() => {
    handleRefresh()
  }, [handleRefresh])

  const handlePatientSelected = useCallback(
    (patientId: string) => {
      history.push(`/clinic/patient/${patientId}`)
    },
    [history],
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageNumber(newPage)
    },
    [setPageNumber],
  )

  const handlePerPageSelect = useCallback(
    (newPerPage: number) => {
      setRecordsPerPage(newPerPage)
      setPageNumber(1)
    },
    [setRecordsPerPage, setPageNumber],
  )

  const handleMrnUpdated = useCallback(
    (value: string) => {
      setMrn(value)
      setPageNumber(1)
    },
    [setMrn, setPageNumber],
  )

  const handlePatientNameUpdated = useCallback(
    (name: string) => {
      setPatientName(name)
      setPageNumber(1)
    },
    [setPatientName, setPageNumber],
  )

  const handleDeliverableUpdated = useCallback(
    (value: boolean) => {
      setDeliverable(value)
      setPageNumber(1)
    },
    [setDeliverable],
  )

  const handleStatusUpdated = useCallback(
    (status: string) => {
      setStatus(status)
      setPageNumber(1)
    },
    [setPageNumber],
  )

  const handleSendInteractionToIntakeAnywhere = useCallback(
    (patientId: string, programInteractionId: string) => {
      sendInteractionToIntakeAnywhere(
        patientId,
        programInteractionId,
        program?.programId,
        pageNumber,
        recordsPerPage,
        patientName,
        mrn,
        isDeliverable,
        status,
        sortField,
        sortDirection,
      )
    },
    [
      isDeliverable,
      mrn,
      pageNumber,
      patientName,
      program?.programId,
      recordsPerPage,
      sendInteractionToIntakeAnywhere,
      sortDirection,
      sortField,
      status,
    ],
  )

  const handleRevokeInteraction = useCallback(
    (patientId: string, programInteractionId: string) => {
      revokeRemoteInteraction(
        patientId,
        programInteractionId,
        program?.programId,
        pageNumber,
        recordsPerPage,
        patientName,
        mrn,
        isDeliverable,
        status,
        sortField,
        sortDirection,
      )
    },
    [
      isDeliverable,
      mrn,
      pageNumber,
      patientName,
      program?.programId,
      recordsPerPage,
      revokeRemoteInteraction,
      sortDirection,
      sortField,
      status,
    ],
  )

  const pagination = {
    pageNumber,
    recordsPerPage,
    totalRecords: total,
    onPageChange: handlePageChange,
    onPerPageSelect: handlePerPageSelect,
  }

  const onSetSort = useCallback(
    (newField: string, newDirection: ISortDirection) => {
      setPageNumber(1)
      setSortField(newField)
      setSortDirection(newDirection)
    },
    [],
  )

  const sorting = useMemo(() => {
    return {
      onSetSort,
      sortDirection,
      sortField,
    }
  }, [onSetSort, sortDirection, sortField])

  return (
    <>
      <Title headingLevel="h2" size="xl">
        Configured Patient Interactions
      </Title>
      <ConfiguredInteractionsToolbar
        patientName={patientName}
        onPatientNameUpdated={handlePatientNameUpdated}
        mrn={mrn}
        onMrnUpdated={handleMrnUpdated}
        onIsDeliverableUpdated={handleDeliverableUpdated}
        isDeliverable={isDeliverable}
        onStatusFilterUpdated={handleStatusUpdated}
        statusFilter={status}
        onRefresh={handleRefresh}
        pagination={pagination}
      />
      {isLoading ? (
        <div>
          <Spinner size="sm" />
        </div>
      ) : interactions.length > 0 ? (
        <ConfiguredInteractionsTable
          interactions={interactions}
          onPatientSelected={handlePatientSelected}
          onSendInteractionToIntakeAnywhere={
            handleSendInteractionToIntakeAnywhere
          }
          onRevokeInteraction={handleRevokeInteraction}
          sorting={sorting}
        />
      ) : (
        <EmptyState>
          <EmptyStateIcon icon={SearchIcon} />
          <Title headingLevel="h5" size="lg">
            No Patient Interactions
          </Title>
        </EmptyState>
      )}
    </>
  )
}
